import { memo, useState, useMemo, useCallback } from 'react';
import { Check } from '../icons/Check';
import { XCircle } from '../icons/XCircle';
import { InlineMarkdownEditor } from '../MarkdownEditor';
import styles from './style/SegmentPersona.module.css';

const DEFAULT_PLACEHOLDER = `Paste or type your persona details here...
Example:

• Voice: Professional, friendly, and supportive
• Behaviors: Responds with empathy, provides actionable advice
• Data: Use customer purchase history and preferences`;

type PersonaFormProps = {
  /** The initial value when the form opens (used to derive hasChanges) */
  initialValue: string;
  /** Placeholder text for the markdown editor */
  placeholder?: string;
  /** Called with the trimmed value when user saves. Return a promise that resolves on success. */
  onSave: (value: string | null) => Promise<void>;
  /** Called when user cancels editing */
  onCancel: () => void;
  /** Whether a save operation is currently in progress */
  isSaving?: boolean;
  /** Height of the markdown editor */
  height?: number;
  /** Accessible label for the save button */
  saveButtonAriaLabel?: string;
  /** Accessible label for the cancel button */
  cancelButtonAriaLabel?: string;
};

/**
 * PersonaForm is a self-contained form component for editing persona text.
 * 
 * It manages its own form state and derives `hasChanges` from comparing
 * the current value to the initial value (no useEffect syncing needed).
 * 
 * The parent provides save/cancel callbacks - the actual API mutation
 * is handled externally, keeping this component generic and reusable.
 * 
 * @example
 * ```tsx
 * <PersonaForm
 *   initialValue={segment.persona || ''}
 *   onSave={async (value) => {
 *     await updatePersonaMutation.mutateAsync(value);
 *   }}
 *   onCancel={() => setIsEditing(false)}
 *   isSaving={updatePersonaMutation.isPending}
 * />
 * ```
 */
const PersonaFormComponent = ({
  initialValue,
  placeholder = DEFAULT_PLACEHOLDER,
  onSave,
  onCancel,
  isSaving = false,
  height = 250,
  saveButtonAriaLabel = 'Save persona',
  cancelButtonAriaLabel = 'Cancel editing persona',
}: PersonaFormProps) => {
  // Form state - initialized from initialValue prop (no useEffect sync needed)
  const [value, setValue] = useState(initialValue);

  // Derive hasChanges with useMemo instead of manual state tracking
  const hasChanges = useMemo(
    () => value !== initialValue,
    [value, initialValue],
  );

  // Handle markdown editor changes
  const handleChange = useCallback((newValue: string) => {
    setValue(newValue);
  }, []);

  // Handle save - trim and convert empty to null
  const handleSave = useCallback(async () => {
    const trimmedValue = value.trim() || null;
    await onSave(trimmedValue);
  }, [value, onSave]);

  // Prevent save when no changes or already saving
  const isSaveDisabled = !hasChanges || isSaving;

  return (
    <div className={styles['persona-editor-container']}>
      <InlineMarkdownEditor
        value={value}
        onChange={handleChange}
        hasChanges={hasChanges}
        isSaving={isSaving}
        showActions={false}
        height={height}
        placeholder={placeholder}
      />
      <div className={styles['persona-edit-actions']}>
        <Check
          disabled={isSaveDisabled}
          onClick={handleSave}
          size={16}
          aria-label={saveButtonAriaLabel}
        />
        <XCircle
          onClick={onCancel}
          size={16}
          aria-label={cancelButtonAriaLabel}
        />
      </div>
    </div>
  );
};

export const PersonaForm = memo(PersonaFormComponent);
